import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MonthlyTurnoverChart from "../components/MonthlyTurnoverChart";
import MonthlyTurnoverTable from "../components/MonthlyTurnoverTable";
import DateSelector from "../components/DateSelector";
import MonthlyViewLayout from "../components/MonthlyViewLayout";
import MonthlyOverviewGrid from "../components/MonthlyOverviewGrid";
import { fetchMonthlyTurnover, fetchMonthlyHoursWorked, fetchMonthlyPlanningCounts } from "../api";
import { toMonthStart } from "../utils/month";
import { formatLocalDate } from "../utils/localDateTime";

const MonthlyOverviewPage = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const rawDate = date || formatLocalDate(new Date());
  const selectedDate = toMonthStart(rawDate);
  const [turnoverRows, setTurnoverRows] = useState([]);
  const [hoursRows, setHoursRows] = useState([]);
  const [planningsRows, setPlanningsRows] = useState([]);
  const [turnoverLoading, setTurnoverLoading] = useState(true);
  const [hoursLoading, setHoursLoading] = useState(true);
  const [planningsLoading, setPlanningsLoading] = useState(true);

  useEffect(() => {
    if (!date || date !== selectedDate) navigate(`/chart/monthly/overview/${selectedDate}`, { replace: true });
  }, [date, selectedDate, navigate]);

  // Reset loading states when selectedDate changes
  useEffect(() => {
    setTurnoverLoading(true);
    setHoursLoading(true);
    setPlanningsLoading(true);
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;

    // Fetch turnover data
    setTurnoverLoading(true);
    fetchMonthlyTurnover(selectedDate)
      .then(setTurnoverRows)
      .catch(() => setTurnoverRows([]))
      .finally(() => setTurnoverLoading(false));

    // Fetch hours data
    setHoursLoading(true);
    fetchMonthlyHoursWorked(selectedDate)
      .then(setHoursRows)
      .catch(() => setHoursRows([]))
      .finally(() => setHoursLoading(false));

    // Fetch plannings data
    setPlanningsLoading(true);
    fetchMonthlyPlanningCounts(selectedDate)
      .then(setPlanningsRows)
      .catch(() => setPlanningsRows([]))
      .finally(() => setPlanningsLoading(false));
  }, [selectedDate]);

  const handleDateChange = useCallback((newDate) => {
    navigate(`/chart/monthly/overview/${newDate}`);
  }, [navigate]);

  // Check if any data is still loading
  const isAnyLoading = turnoverLoading || hoursLoading || planningsLoading;

  // Compute monthly totals for badges
  const totalTurnoverOps = turnoverRows.reduce((sum, r) => sum + (r.operation_turnover || 0), 0);
  const totalTurnoverHours = turnoverRows.reduce((sum, r) => sum + (r.hourly_turnover || 0), 0);
  const totalOps = planningsRows.filter(r => r.workcell_id !== "Total").reduce((sum, r) => sum + (r.total_completed || 0), 0);
  const totalHours = hoursRows.reduce((sum, r) => sum + (r.active_hours || 0), 0);
  const totalPossibleHours = hoursRows.reduce((sum, r) => sum + (r.wallclock_hours || 0), 0);
  const avgActivity = totalPossibleHours > 0 ? (totalHours / totalPossibleHours) * 100 : 0;

  return (
    <MonthlyViewLayout date={selectedDate}>
      <DateSelector date={selectedDate} setDate={handleDateChange} disabled={isAnyLoading} mode="month" />
      <MonthlyOverviewGrid
        totalTurnoverOps={totalTurnoverOps}
        totalOps={totalOps}
        totalTurnoverHours={totalTurnoverHours}
        totalHours={totalHours}
        avgActivity={avgActivity}
        totalPossibleHours={totalPossibleHours}
      />
      <MonthlyTurnoverChart date={selectedDate} />
      <MonthlyTurnoverTable date={selectedDate} />
    </MonthlyViewLayout>
  );
};

export default MonthlyOverviewPage;
